<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, onMounted, PropType, ref, watch } from "vue";
import { Trainer, Transaction } from "../../types/PersologOnlineTypes.ts";
import { ProfileAdministrationAPI } from "../../api";
import TransactionTable from "./TransactionTable.vue";
import ReclaimDialog from "./ReclaimDialog.vue";
import { PROFILE_RECLAIM_FLAG, useFeatureFlag } from "../../composables/useFeatureFlag.ts";
import { compareDesc } from "date-fns";
import { adjustDateTimes, initializeDates, isMobileView, toBackendDateTime } from "../../utils/GeneralUtils.ts";

const { t } = useI18n();

const props = defineProps({
    visibleToggle: {
        type: Boolean,
        default: false
    },
    account: {
        type: Object as () => Trainer,
        required: true
    },
    texts: {
        type: Object as () => Record<string, string>,
        required: true
    },
    // The distributor looking at the sub-account. Only set from the sub-account table, and
    // only then can profiles be taken back from here (RDT-351).
    trainer: {
        type: Object as PropType<Trainer>,
        default: undefined
    }
});

const visible = ref(props.visibleToggle);
const transactions = ref<Transaction[]>([]);
const dates = ref<Date[]>(adjustDateTimes(initializeDates()));
const profileReclaimEnabled = useFeatureFlag(PROFILE_RECLAIM_FLAG);
const showReclaimDialog = ref(false);

const canReclaim = computed(
    () => profileReclaimEnabled.value && !!props.trainer && props.trainer.id !== props.account.id
);

const emit = defineEmits(["update:visible"]);

watch(
    () => visible.value,
    () => {
        emit("update:visible", visible.value);
    }
);

watch(
    () => props.visibleToggle,
    () => {
        visible.value = props.visibleToggle;
    }
);

watch(
    () => dates.value,
    async () => {
        dates.value = adjustDateTimes(dates.value);
        await fetchTransactions(props.account);
    }
);

async function fetchTransactions(trainer: Trainer) {
    transactions.value = (await ProfileAdministrationAPI.getTransactions(
        trainer.id,
        toBackendDateTime(dates.value[0]),
        toBackendDateTime(dates.value[1])
    )).map((transaction) => ({
        product: transaction.product,
        quantity: transaction.quantity,
        id: transaction.id,
        trainer: transaction.trainer,
        type: transaction.type,
        oldBalance: transaction.oldBalance,
        newBalance: transaction.newBalance,
        date: transaction.date,
        productDescription: props.texts[transaction.product.productNo] ? `${props.texts[transaction.product.productNo]} (${transaction.product.productNo})` : transaction.product.productNo
    } as Transaction));
}

/**
 * A retrieval writes a transaction on the sub-account side, so reload the list the dialog shows.
 */
async function onReclaimed() {
    await fetchTransactions(props.account);
    transactions.value.sort((a, b) => compareDesc(a.date, b.date));
}

onMounted(async () => {
    await fetchTransactions(props.account);
    transactions.value.sort((a, b) => compareDesc(a.date, b.date));
});
</script>
<template>
    <Dialog
        v-model:visible="visible"
        modal
        :header="t('header.accountSettings.transactions') + ' - ' + props.account?.firstname + ' ' + props.account?.lastname"
        position="center"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <div class="flex flex-wrap items-center justify-center gap-3 mb-4 mt-4">
            <DatePicker
                v-model="dates"
                selection-mode="range"
                show-icon
                icon-display="input"
                :manual-input="true"
                :touch-u-i="isMobileView()"
                show-button-bar
                :number-of-months="isMobileView() ? 1 : 2"
                :date-format="t('formats.datePicker')"
                :max-date="new Date(Date.now())"
            />
            <Button
                v-if="canReclaim"
                :id="'transaction-dialog_btn_reclaim_' + props.account.id"
                icon="pi pi-history"
                :label="t('buttons.reclaim')"
                v-tooltip="t('header.accountSettings.accountLinks.tooltips.reclaim')"
                outlined
                size="small"
                @click="showReclaimDialog = true"
            />
        </div>
        <div class="form-container">
            <TransactionTable
                headline=""
                :transactions="transactions ?? []"
                :with-icon="true"
                :rows="10"
            />
        </div>
    </Dialog>
    <ReclaimDialog
        v-if="showReclaimDialog && profileReclaimEnabled && trainer"
        :visible-toggle="showReclaimDialog"
        :account="props.account"
        :trainer="trainer"
        @update:visible="(value) => (showReclaimDialog = value)"
        @retrieved="onReclaimed"
    />
</template>
